import { Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CrossReferenceService } from '../../../services/mbrs/cross-reference.service';

/** One rendered date-range box inside the band. */
export interface FinancialPeriodRow {
  /** Which financial period this box represents. */
  period: 'current' | 'previous';
  /** Formatted range, e.g. "01/01/2024 - 31/12/2024" (never empty). */
  range: string;
  /** "Current"/"Previous" — empty when only one box renders (a lone box needs no caption). */
  caption: string;
}

/**
 * Pure builder for the band's rows. DI-free and TestBed-free so the
 * caption/empty-drop logic unit-tests in isolation (see the matching `.spec.ts`),
 * mirroring the `buildJsonTreeNodes` convention in this folder.
 *
 * @param periods  Requested periods, rendered in this order.
 * @param resolve  Maps a period to its formatted range ('' when the filing has
 *                 no dates for that period — e.g. CrossReferenceService.getFormattedFinancialYear).
 */
export function buildPeriodRows(
  periods: ReadonlyArray<'current' | 'previous'>,
  resolve: (period: 'current' | 'previous') => string,
): FinancialPeriodRow[] {
  const present = periods
    .map((period) => ({ period, range: (resolve(period) ?? '').trim() }))
    .filter((row) => row.range !== '');

  // A single box is self-explanatory; only caption when 2+ boxes share the band.
  const showCaptions = present.length > 1;

  return present.map(({ period, range }) => ({
    period,
    range,
    caption: showCaptions ? (period === 'current' ? 'Current' : 'Previous') : '',
  }));
}

/**
 * FinancialPeriodBandComponent
 *
 * A reusable, display-only band that surfaces the filing's financial-period
 * date range(s) directly beneath a sheet's grey label header — see role 7
 * (Auditor's Report). The range(s) come from the Filing Information (Role-1)
 * DEI block via the shared {@link CrossReferenceService}, the same single
 * source of truth used by the dimension tables (role 23/24) and SOCIE (role 19),
 * so the period label stays consistent across every sheet.
 *
 * Self-contained: a sheet only drops the tag in and chooses its periods —
 * no per-sheet getter, markup, or CSS.
 *
 * Usage:
 *   <app-financial-period-band [periods]="['current']"></app-financial-period-band>
 *   <app-financial-period-band [periods]="['current', 'previous']"></app-financial-period-band>
 */
@Component({
  selector: 'app-financial-period-band',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './financial-period-band.component.html',
  styleUrl: './financial-period-band.component.css',
})
export class FinancialPeriodBandComponent {
  private crossRefService = inject(CrossReferenceService);

  /**
   * Which periods to render, in display order. Each period whose range resolves
   * empty is dropped, so requesting `['current', 'previous']` on a single-period
   * filing gracefully shows just the current box.
   */
  @Input() periods: ('current' | 'previous')[] = ['current'];

  /**
   * Rows to render. A getter (not memoised) so the band re-reads the cross-ref
   * snapshot on each change-detection pass — matching how role 23/24 bind their
   * period header, so an edit to Filing Information flows through immediately.
   */
  get rows(): FinancialPeriodRow[] {
    return buildPeriodRows(this.periods, (period) =>
      this.crossRefService.getFormattedFinancialYear(period),
    );
  }

  /** trackBy keeps the at-most-two boxes stable across CD passes. */
  trackByPeriod = (_index: number, row: FinancialPeriodRow): string => row.period;
}
